import type { ItineraryResourceEntryContext } from "../../../shared/itinerary-resource-entry";
/** 导入时冻结的来源与处理清单，不随来源核价后续修改。 */
export type FulfillmentBudgetImportReport = {
  budget_order_id: string; version_no: number; version_name: string;
  source_revision: string; source_updated_at: string; source_total_amount: string;
  imported_at: string; imported_by: number; imported_count: number; imported_amount: string;
  issues: Array<{ source_line_id: string; line_no: number; category: string; name: string; amount: string; reason: string }>;
  line_links?: Array<{ line_id: string; source_line_id: string; source_amount: string }>;
};

/** 仅承载前端已验证、可直接展示和执行的确认件业务提示。 */
export class FulfillmentOrderUserFacingError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "FulfillmentOrderUserFacingError";
  }
}

/** 确认件列表筛选。 */
export type FulfillmentOrderListFilters = {
  q?: string;
  status?: string;
  supplierId?: string;
  itineraryId?: string;
};

const FULFILLMENT_RESOURCE_TYPE_ALIASES: Readonly<Record<string, string>> = {
  stay: "住宿",
  meal: "餐",
  transport: "大交通",
  ticket: "考察点",
  service: "杂费",
  大巴: "包车",
  门票: "考察点",
  餐饮: "餐",
  其他: "杂费",
};

/** 将历史资源类型归一为当前中文契约值，未知值保持原样。 */
export function normalizeFulfillmentResourceType(resourceType: string): string {
  const normalized = resourceType.trim();
  return FULFILLMENT_RESOURCE_TYPE_ALIASES[normalized] ?? normalized;
}

export type FulfillmentOrderSortField = "updated_at" | "created_at" | "current_amount" | "order_no";
export type FulfillmentOrderSortDirection = "asc" | "desc";

export type FulfillmentResourceMentionType = "住宿" | "考察点" | "大交通" | "包车";
export type FulfillmentResourceMentionLevel = "spu" | "sku";
export type FulfillmentResourceTransportMode = "rail" | "air";

/** 保存前由服务端最终解析的资源选择。 */
export type FulfillmentOrderResourceSelection =
  | {
      kind: "transport";
      transportMode: FulfillmentResourceTransportMode;
      fromPointId: string;
      toPointId: string;
    }
  | {
      kind: "stay";
      staySpuId: string;
      skuName: string;
    }
  | {
      kind: "bus";
      vehicleSpecName: string;
      entrySemantic: "pickup" | "dropoff" | "local_charter" | "transfer";
    }
  | {
      kind: "point";
      pointSpuId: string;
      skuName: string;
    };

/** 确认件大交通起终点候选。 */
export type FulfillmentTransportPoint = {
  id: string;
  name: string;
  shortName: string | null;
  pointType?: string | null;
  city?: string | null;
};

/** 确认件 @ 候选，SKU 项同时携带完整父级引用。 */
export type FulfillmentResourceMentionCandidate = {
  resourceType: FulfillmentResourceMentionType;
  level: FulfillmentResourceMentionLevel;
  mentionText: string;
  spuTableName: string | null;
  spuId: string | null;
  spuName: string | null;
  skuTableName: string | null;
  skuId: string | null;
  skuName: string | null;
  description: string | null;
  transportMode: FulfillmentResourceTransportMode | null;
  travelerRole: string | null;
  vehicleSpecName?: string | null;
  roomCapacity?: number | null;
  referenceUnitPrice?: string | null;
  referencePriceDate?: string | null;
  referencePriceType?: string | null;
  referencePriceCurrency?: string | null;
};

/** 精确起终点下可关联的大交通路线与规格候选。 */
export type FulfillmentTransportResourceCandidate = {
  transportMode: FulfillmentResourceTransportMode;
  fromPointId: string;
  fromPointName: string;
  toPointId: string;
  toPointName: string;
  seatClass: string | null;
  spuTableName: string;
  spuId: string;
  spuName: string;
  skuTableName: string;
  skuId: string;
  skuName: string;
  travelerRole: string | null;
  description: string | null;
};

/** 确认件列表项，金额始终保留字符串契约。 */
export type FulfillmentOrderListItem = {
  id: string;
  orderNo: string;
  workspaceId: string;
  sourceBudgetOrderId: string;
  sourceBudgetOrderNo: string;
  itineraryId: string;
  itineraryTitle: string;
  settlementSupplierId: string;
  settlementSupplierName: string;
  currentVersionId: string | null;
  currentVersionNo: number | null;
  currentAmount: string;
  status: string;
  unassignedLineCount: number;
  orderCount: number;
  createdAt: string;
  updatedAt: string;
};

export type FulfillmentOrderListResult = {
  items: FulfillmentOrderListItem[];
  total: number;
  page: number;
  pageSize: number;
};

/** 行程中的真实考察点节点，同一资源的重复到访保留各自节点。 */
export type FulfillmentItineraryPoint = {
  nodeId: string;
  pointSpuId: string;
  pointName: string;
  dayNo: number;
  timeSlot: string;
  sortOrder: number;
};

/** 工作区头信息，revision 对应服务端 updated_at。 */
export type FulfillmentWorkspace = {
  budgetImport?: FulfillmentBudgetImportReport | null;
  id: string;
  sourceBudgetOrderId: string;
  sourceBudgetOrderNo: string;
  sourceBudgetVersionNo: number;
  sourceBudgetUpdatedAt: string;
  sourceBudgetTotalAmount: string;
  itineraryId: string;
  projectId?: string | null;
  customerQuoteId?: string | null;
  itineraryTitle: string;
  serviceDateFrom: string;
  serviceDateTo: string;
  status: string;
  revision: string;
  currentVersionId?: string | null;
  currentVersionNo?: number;
  lineNoHighWater?: number;
  routeDays?: Array<{ dayNo: number; date: string | null; stayName?: string | null }>;
  itineraryPoints?: FulfillmentItineraryPoint[];
  peopleCounts?: { studentCount: number | null; teacherCount: number | null; guideCount: number | null; driverCount: number | null } | null;
};

export type FulfillmentOrderLine = {
  id: string;
  workspaceId: string;
  fulfillmentOrderId: string | null;
  sourceBudgetLineId: string | null;
  sourceType: string;
  lineNo: number;
  logicalLineId?: string;
  originType?: "version_base" | "post_finalization_add";
  introducedAfterVersionId?: string | null;
  originChangeBatchId?: string | null;
  incorporatedVersionId?: string | null;
  resourceType: string;
  spuTableName?: string | null;
  spuId?: string | null;
  spuNameSnapshot?: string | null;
  skuTableName?: string | null;
  skuId?: string | null;
  skuNameSnapshot?: string | null;
  resourceSelection?: FulfillmentOrderResourceSelection | null;
  /** 由真实交通 SPU 或明确人工飞机依据派生的只读分类，不参与资源写入。 */
  transportMode?: "rail" | "air" | "ship" | null;
  itemNameSnapshot: string;
  itemSpecSnapshot?: string | null;
  fulfillmentSupplierId?: string | null;
  fulfillmentSupplierName?: string | null;
  procurementChannelType?:
    | "direct_resource_provider"
    | "ground_operator"
    | "other_intermediary"
    | "unknown";
  procurementSourceType?: string | null;
  procurementSourceId?: string | null;
  providerAssignmentStatus?: "not_applicable" | "unassigned" | "assigned";
  settlementSupplierId?: string | null;
  settlementSupplierName?: string | null;
  serviceDateFrom?: string | null;
  serviceDateTo?: string | null;
  dayExpression?: string;
  dayNumbers?: number[];
  serviceDates?: string[];
  dateMappingStatus?: "mapped" | "needs_adjustment";
  reviewStatus?: "pending" | "confirmed";
  travelerRole?: string | null;
  mealType?: string | null;
  pricingUnitType: string;
  quantity: string;
  billingCount: string;
  unit: string;
  unitPriceAmount: string;
  amount: string;
  sourceBudgetAmount?: string | null;
  diffAmount?: string | null;
  diffReason?: string | null;
  status: string;
  remark?: string | null;
};

export type FulfillmentOrder = {
  id: string;
  orderNo: string;
  workspaceId: string;
  sourceBudgetOrderId: string;
  itineraryId: string;
  settlementSupplierId: string;
  settlementSupplierName: string;
  supplierContactName: string | null;
  supplierContactPhone: string | null;
  currentVersionId: string | null;
  currentVersionNo: number | null;
  /** 供应商应付内部文档修订号，仅用于写请求并发控制。 */
  documentRevision?: number;
  baselineAmount: string;
  currentAmount: string;
  status: string;
  updatedAt: string;
};

export type FulfillmentOrderVersion = {
  id: string;
  fulfillmentOrderId: string;
  versionNo: number;
  previousVersionId: string | null;
  versionType: string;
  status: string;
  settlementSupplierId: string;
  settlementSupplierName: string;
  amount: string;
  changeSummary: string | null;
  archiveCount?: number;
  createdAt: string;
};

export type FulfillmentSourceDiffLine = {
  sourceBudgetLineId: string;
  sourceBudgetAmount: string;
  currentAmount: string;
  diffAmount: string;
  activeLineCount: number;
  unassignedLineCount: number;
  assignedLineCount: number;
  changeTypes: string[];
  diffReasons: string[];
};

export type FulfillmentSourceDiff = {
  workspaceId: string;
  sourceBudgetTotalAmount: string;
  currentTotalAmount: string;
  diffTotalAmount: string;
  manualLineCount: number;
  manualAmount: string;
  unassignedLineCount: number;
  hasChanges: boolean;
  lines: FulfillmentSourceDiffLine[];
};

export type FulfillmentWorkspaceEditor = {
  workspace: FulfillmentWorkspace;
  lines: FulfillmentOrderLine[];
  orders: FulfillmentOrder[];
  sourceDiff: FulfillmentSourceDiff;
  quickEntryContext: ItineraryResourceEntryContext | null;
  quickEntryContextError: string | null;
};

export type EnterFulfillmentWorkspaceResult = {
  workspaceId: string;
  created: boolean;
};

export type SaveFulfillmentWorkspaceRequest = {
  expectedUpdatedAt: string;
  lines: FulfillmentOrderLine[];
};

/** 保存完整未归口草稿并原子生成选中行协议的请求。 */
export type GenerateSelectedFulfillmentOrdersRequest = {
  expectedUpdatedAt: string;
  lines: FulfillmentOrderLine[];
  selectedLineNos: number[];
  settlementSupplierId: string;
  /** 导游合同重生成：作废同结算主体草稿协议后按本次勾选重建。 */
  replaceDraftAgreement?: boolean;
};

export type FulfillmentOrderLineAdjustment = {
  quantity: string;
  billingCount: string;
  specUnit: "晚" | "次" | "人";
  unitPriceAmount: string;
};

/** 已定稿原行仅允许结构化增减；添加录入独立追加新行和账本。 */
export type ApplyFulfillmentOrderChangeRequest = {
  expectedRevision: number;
  idempotencyKey: string;
  reason: string;

  addLines: FulfillmentOrderLine[];
  adjustment?: (FulfillmentOrderLineAdjustment & { sourceLineId: string }) | null;
};

export type FulfillmentOrderItineraryDay = {
  dayNo: number;
  date: string | null;
  locationSummary: string;
  stayName: string | null;
  routeSummary: string;
};

export type FulfillmentOrderDetail = {
  itineraryId?: string;
  companyPartyName?: string;
  companyContactName?: string | null;
  companyContactPhone?: string | null;
  itineraryDays?: FulfillmentOrderItineraryDay[];
  order: FulfillmentOrder;
  currentVersion: FulfillmentOrderVersion | null;
  versions: FulfillmentOrderVersion[];
  lines: FulfillmentOrderLine[];
  baseline?: FulfillmentOrderContractBaseline | null;
  archives?: FulfillmentOrderArchive[];
  changes?: FulfillmentOrderChangeBatch[];
};

export type FulfillmentOrderContractBaselineLine = {
  sourceLineId: string;
  lineNo: number;
  amount: string;
  snapshot: Record<string, unknown>;
};

export type FulfillmentOrderContractBaseline = {
  id: string;
  documentRevision: number;
  baselineRevision: number;
  baselineAmount: string;
  contentHash: string;
  backfillStatus: string;
  signedAt: string;
  firstArchiveId: string | null;
  snapshot: Record<string, unknown>;
  lines: FulfillmentOrderContractBaselineLine[];
};

export type FulfillmentOrderChangeItem = {
  id: string;
  sequenceNo: number;
  action: "add" | "delete" | "adjust";
  sourceLineId: string | null;
  replacementLineId: string | null;
  baseVersionLineId?: string | null;
  amount: string;
  snapshot: Record<string, unknown>;
};

export type FulfillmentOrderChangeBatch = {
  id: string;
  orderId?: string | null;
  baseVersionId?: string | null;
  changeType?:
    | "amount_adjustment"
    | "structured_adjustment"
    | "main_item_added"
    | "main_item_changed"
    | "main_item_deleted"
    | "legacy_change";
  expectedRevision: number;
  revisionAfter: number;
  idempotencyKey: string;
  reason: string;
  amountBefore: string;
  amountAfter: string;
  deltaAmount: string;
  hasFulfillmentSupplierChange: boolean;
  hasSettlementSupplierChange: boolean;
  actorId: number | null;
  createdAt: string;
  items: FulfillmentOrderChangeItem[];
};

export type FulfillmentLineFilters = {
  resourceType: string;
  assignment: "all" | "unassigned" | "assigned";
  sourceDifferenceOnly: boolean;
};

export type SettlementSupplierDraft = {
  id: string;
  name: string;
  fulfillmentSameAsSettlement: boolean;
};

/** 权威供应商主数据候选。 */
export type FulfillmentSupplierOption = {
  id: string;
  code: string;
  name: string;
  shortName?: string | null;
  type: string;
  subjectNature?: "individual" | "organization" | "internal" | "unknown" | null;
  companySubjectName?: string | null;
  canBeSettlementSubject: boolean;
  canBeFulfillmentSubject: boolean;
  contactName?: string | null;
  contactPhone?: string | null;
};

export type FulfillmentSupplierUsage = "settlement" | "fulfillment";

export type FulfillmentOrderArchive = {
  id: string;
  fulfillmentOrderId: string;
  versionId: string;
  originalFileName: string;
  mimeType: string;
  fileSize: number;
  fileSha256: string;
  status: string;
  archiveVersion?: number;
  documentRevision?: number;
  replacedById?: string | null;
  archivedAt: string;
  downloadUrl?: string;
};

/** 确认件编辑页的八类展示分类，统一底层确认行不按视图拆表。 */
export type ConfirmationCategory = "stay" | "train" | "air" | "ship" | "bus" | "ticket" | "meal" | "crew" | "misc";

/** 普通草稿的原子批量变更，保持新增、修改与删除处于同一次本地更新。 */
export type ConfirmationBatchChange = {
  updates: Array<{ id: string; patch: Partial<FulfillmentOrderLine> }>;
  additions: Partial<FulfillmentOrderLine>[];
  removals: string[];
};
